import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Languages, ArrowRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { getSettings } from "../lib/store";

// Navigate to a section on the homepage from any page
function useSectionNav() {
  const navigate = useNavigate();
  const location = useLocation();

  return function goTo(sectionId) {
    if (location.pathname === "/") {
      // Already on homepage — just scroll
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      // Navigate to homepage then scroll after render
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 350);
    }
  };
}

export function Navbar() {
  const { t, lang, toggleLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const goTo = useSectionNav();
  const settings = getSettings();
  const whatsappPhone = settings?.whatsapp_phone ? settings.whatsapp_phone.replace(/\D/g, "") : "";
  const whatsappUrl = settings?.whatsapp || (whatsappPhone ? `https://wa.me/${whatsappPhone}` : "#");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { section: "home",       label: t.nav.home },
    { section: "products",   label: t.nav.products },
    { section: "services",   label: t.nav.services },
    { section: "references", label: t.nav.references },
    { section: "about",      label: t.nav.about },
    { section: "contact",    label: t.nav.contact },
  ];

  function handleLink(section) {
    setOpen(false);
    goTo(section);
  }

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 bg-[#0d1b2a] text-white transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_24px_rgba(0,0,0,0.18)]" : ""
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between gap-6">

          {/* Logo → always goes to homepage */}
          <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo-link">
            <img
              src="/logo.png"
              alt="YGS Yapı Güvenlik Sistemleri"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <button
                key={l.section}
                onClick={() => handleLink(l.section)}
                data-testid={`nav-link-${l.section}`}
                className="text-sm font-medium text-white/80 hover:text-[#ffb703] transition-colors duration-200 bg-transparent border-none cursor-pointer"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="hidden sm:flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/80 hover:text-[#ffb703] transition-colors px-2 py-1 border border-white/15 rounded-md"
              aria-label="Toggle language"
            >
              <Languages size={14} />
              {lang.toUpperCase()}
            </button>

            {/* CTA */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="navbar-cta"
              className="hidden md:inline-flex items-center gap-2 bg-[#ffb703] text-[#0d1b2a] font-bold px-4 py-2.5 rounded-md hover:scale-[1.02] transition-transform text-sm"
            >
              {t.nav.getQuote}
              <ArrowRight size={16} />
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setOpen((p) => !p)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-white"
              aria-label="Open menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-white/10 py-4 flex flex-col gap-1"
          >
            {links.map((l) => (
              <button
                key={l.section}
                onClick={() => handleLink(l.section)}
                data-testid={`mobile-nav-link-${l.section}`}
                className="w-full text-left px-2 py-3 text-sm font-medium text-white/80 hover:text-[#ffb703] transition-colors bg-transparent border-none cursor-pointer"
              >
                {l.label}
              </button>
            ))}

            {/* Mobile WhatsApp CTA */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 mx-2 flex items-center justify-center gap-2 bg-[#ffb703] text-[#0d1b2a] font-bold px-4 py-3 rounded-md text-sm"
            >
              {t.nav.getQuote}
              <ArrowRight size={16} />
            </a>

            <button
              onClick={toggleLang}
              data-testid="mobile-lang-toggle"
              className="self-start mt-2 mx-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/80 px-3 py-2 border border-white/15 rounded-md"
            >
              <Languages size={14} /> {lang.toUpperCase()}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
